/**
 * @author NikolaV
 */
import { IMapTilelayer, TileLayerService } from '@openlayers/tile-layers/TileLayerService';
import OlMap from 'ol/Map';
import { computed, makeObservable, observable } from 'mobx';
import { unByKey } from 'ol/Observable';
import TileLayer from 'ol/layer/Tile';
import TileImage from 'ol/source/TileImage';
import TileWMS from 'ol/source/TileWMS';
import OSMSource from 'ol/source/OSM';
import XYZSource from 'ol/source/XYZ';
import WMTSCapabilities from 'ol/format/WMTSCapabilities';
import WMTS, { optionsFromCapabilities } from 'ol/source/WMTS';
import { splitAuthStringFromURL } from '@shared/utils/utils';
import { PageStateUtils } from '@shared/user-state/PageStateUtils';
import TileSource from 'ol/source/Tile';
import { tileLoadFunction } from '@openlayers/tile-layers/TileLayerAuthService';


const wmtsParser = new WMTSCapabilities();


export class MapTileLayers implements IStateful<ITileLayersState> {

    @observable layersDefinitions: IMapTilelayer[] = [];
    @observable activeUuids = [];
    @observable opacities = {};
    readonly defaultOrder = [];

    layers: TileLayer<TileSource>[] = [];

    // connection problems
    eventKeys: {
        [key: string]: (OlEventsKey | OlEventsKey[])[]
    } = {};
    @observable connectionProblem: { [key: string]: boolean } = {};

    @computed
    get hasConnectionProblem(): boolean {
        return Object.values(this.connectionProblem).some(v => !!v);
    }

    constructor(
        private mapInstance: OlMap,
        tileLayerDefinitions: IMapTilelayer[])
    {
        makeObservable(this);
        this.layersDefinitions = tileLayerDefinitions;
        this.defaultOrder = tileLayerDefinitions.map(l => l.uuid);

        this.opacities = this.layersDefinitions.reduce((obj, def) => {
            obj[def.uuid] = 100;
            return obj;
        }, {});

        this.connectionProblem = this.layersDefinitions.reduce((obj, def) => {
            obj[def.uuid] = false;
            return obj;
        }, {});

        this.layers = this.layersDefinitions.map(def => {
            const obj = {
                uuid: def.uuid,
                id: def.id,
                fileUuid: def.fileUuid,
                url: def.url,
                isProxy: def.isProxy
            };

            // InfrakitProvider is set to true for internal map base layers
            let providerType = "direct";
            if (def.url && kuura.isInfrakitMapProvider(def.url)) {
                providerType = "infrakitProvider";
            } else if (def.isProxy) {
                providerType = "proxy";
            }

            if (def.type === 'STATIC_IMAGE') {
                return new TileLayer({
                    source: new TileImage({ url: def.url, crossOrigin: 'anonymous' }),
                    ...obj
                });
            }

            if (def.type === 'OSM_TILE') {
                return new TileLayer({
                    source: new OSMSource({ url: def.url, maxZoom: def.maxZoom, crossOrigin: 'anonymous' }),
                    ...obj
                });
            }

            if (def.type === 'XYZ_TILE') {
                // This is called for our own map tiles or other authenticated layers to add auth in the headers
                if (providerType === "infrakitProvider" || providerType === "proxy") {
                    return new TileLayer({
                        source: new XYZSource({
                            url: def.url,
                            maxZoom: def.maxZoom,
                            crossOrigin: 'anonymous',
                            wrapX: false,
                            tileLoadFunction: async (imageTile, src) => {
                                await tileLoadFunction(imageTile, src, providerType, obj.uuid, null);
                            }
                        }),
                        ...obj
                    });
                }

                return new TileLayer({
                    source: new XYZSource({
                        url: def.url,
                        maxZoom: def.maxZoom,
                        crossOrigin: 'anonymous',
                        wrapX: false
                    }),
                    ...obj
                });
            }
            if (def.type === 'WMS_TILE') {
                let baseUrl = def.url;
                if (def.url != null) {
                    baseUrl = def.url.split('?')[0];
                }

                const wmsParams = {
                    WIDTH: 256, HEIGHT: 256, FORMAT: 'image/png',
                    TILED: !def.url?.toLowerCase().includes('tiled=false'),
                    ...def.wmsParams,
                };

                const [key, keyValue] = splitAuthStringFromURL(def.url);
                if (key) {
                    wmsParams[key] = keyValue;
                }

                if (providerType === "infrakitProvider" || providerType === "proxy") {
                    return  new TileLayer({
                        visible: true,
                        source: new TileWMS({
                            url: baseUrl,
                            serverType: 'geoserver',
                            crossOrigin: 'anonymous',
                            hidpi: false, // some wms servers dont support non-256px tiles so it may fail per-user if enabled
                            params: wmsParams,
                            tileLoadFunction: async (imageTile, src) => {
                                await tileLoadFunction(imageTile, src, providerType, obj.uuid, null);
                            }
                        }),
                        ...obj
                    });
                }

                return  new TileLayer({
                    visible: true,
                    source: new TileWMS({
                        url: baseUrl,
                        serverType: 'geoserver',
                        crossOrigin: 'anonymous',
                        hidpi: false, // some wms servers dont support non-256px tiles so it may fail per-user if enabled
                        params: wmsParams
                    }),
                    ...obj
                });
            }

            if (def.type === 'WMTS_TILE') {
                try {
                    const capabilities = TileLayerService.WmtsCapabilities.decompressUnparsed(def.wmsParams);
                    if (!Object.keys(capabilities).length) {
                        return null;
                    }

                    if (providerType === "infrakitProvider" || providerType === "proxy") {
                        return new TileLayer({
                            visible: true,
                            source: new WMTS({
                                ...optionsFromCapabilities(capabilities, {
                                    layer: capabilities.ACTIVE_LAYER,
                                    matrixSet: capabilities.Contents.TileMatrixSet[0].Identifier,
                                    crossOrigin: true
                                }),
                                tileLoadFunction: async (imageTile, src) => {
                                    await tileLoadFunction(imageTile, src, providerType, obj.uuid, null);
                                }
                            }),
                            ...obj
                        });
                    }

                    return new TileLayer({
                        visible: true,
                        source: new WMTS(optionsFromCapabilities(capabilities, {
                            layer: capabilities.ACTIVE_LAYER,
                            matrixSet: capabilities.Contents.TileMatrixSet[0].Identifier,
                            crossOrigin: true
                        })),
                        ...obj
                    });
                } catch (e) {
                    console.warn('Failed to initialize WMTS layer. Possibly faulty WMTS parameters?', e);
                    return null;
                }
            }
        }).filter(Boolean);
    }

    toggleLayer = (uuid: string) => {
        if (this.activeUuids.includes(uuid)) {
            this.deactivateLayer(uuid);
        }
        else {
            this.activateByIdOrUuid(uuid, false);
        }
    }

    public activateByIdOrUuid = (idOrUuid: string | number, removeOthers = true, opacity = 100) => {
        if (typeof idOrUuid === 'number') {
            idOrUuid = String(idOrUuid);
        }

        let layer = this.getLayerByUuidOrId(idOrUuid);
        if (!layer) {
            // dont fallback to kuura.getDefaultTileLayerUuid() here, it will load tiles for a couple of seconds on page load before real layer is activated from state
            // layers[0] is fine since its always 'Blank' and doesn't load anything
            layer = this.layers[0];
        }

        this.activateLayer(layer, removeOthers, opacity);
    };

    activateLayer = (layer: TileLayer<TileImage>, removeOthers = true, opacity = 100) => {
        if (!layer) return;

        if (removeOthers) {
            this.deactivateAll();
        }

        // add layer to map
        try {
            this.mapInstance.addLayer(layer);
            const uuid = layer.get('uuid');
            const url = layer.get('url');
            this.activeUuids = [uuid, ...this.activeUuids]; // dont use push, doesn't work with observable for soe reason, all make it first so its on top
            this.connectionProblem[uuid] = false;
            this.setOpacity(uuid, opacity);
            this.resortLayersZindex();

            // listen events
            this.eventKeys[uuid] = [
                layer.getSource().on('tileloaderror', (event) => {
                    this.connectionProblem[uuid] = !kuura.isInfrakitMapProvider(url);
                }),
                layer.getSource().on('tileloadend', () => this.connectionProblem[uuid] = false)
            ];
        } catch {} // throws when adding duplicate
    };

    public deactivateLayer = (uuid: string) => {
        const layer = this.getLayerByUuidOrId(uuid);
        if (layer) {
            this.mapInstance.removeLayer(layer);
            this.activeUuids = this.activeUuids.filter(el => el != uuid);
            this.connectionProblem[uuid] = false;

            if (this.eventKeys[uuid]) {
                this.eventKeys[uuid].forEach(unByKey);
                this.eventKeys[uuid] = [];
            }
        }
    }

    public setOpacity = (uuid: string, opacity: number) => {
        const layer = this.getLayerByUuidOrId(uuid);
        if (layer) {
            if (typeof opacity === 'number') {
                const safeValue = utils.clamp(20, opacity, 100);
                layer.setOpacity(safeValue / 100);
                this.opacities[uuid] = safeValue;
            }
            else {
                layer.setOpacity(1);
                this.opacities[uuid] = 100;
            }
        }
    }

    public resortLayersZindex = () => {
        this.activeUuids.forEach((uuid, index) => {
            const layer = this.getLayerByUuidOrId(uuid);
            if (layer) {
                layer.setZIndex(-10 - index);
            }
        })
    }

    public deactivateAll = () => {
        this.activeUuids.forEach(uuid => this.deactivateLayer(uuid));
    }

    public getLayerByUuidOrId = (uuid: string | number): TileLayer<any> => {
        let layer = this.layers.find((l: any) => {
            return l.get('uuid') == uuid || l.get('fileUuid' /* source file for generated geotiff */) == uuid
        });
        if (!layer) {
            layer = this.layers.find((l: any) => l.get('id') == uuid); // legacy user-states use id's
        }
        return layer;
    }

    stateful = {
        defaultState: {
            activeUuids: [kuura.getDefaultTileLayerUuid()],
            opacities: {}
        },

        getCurrentState: (): any => {
            const non100opacities = {};
            Object.entries(this.opacities).forEach(([key, val]) => {
                if (val !== 100 && this.activeUuids.includes(key)) {
                    non100opacities[key] = val;
                }
            });

            return {
                activeUuids: this.activeUuids,
                opacities: non100opacities
            };
        },

        restoreDefaultState: () => {
            this.stateful.restoreState(this.stateful.defaultState);
        },

        restoreState: (state: ITileLayersState): void => {
            if (!state) return null;

            if (PageStateUtils.stateHasKey<any>(state, 'activeUuids')) {
                this.deactivateAll();
                state.activeUuids.reverse().forEach(uuid => {
                    this.activateByIdOrUuid(uuid, false);
                });
            }

            if (PageStateUtils.stateHasKey<any>(state, 'opacities')) {
                Object.entries(state.opacities).forEach(([key, val]) => {
                    if (this.opacities.hasOwnProperty(key)) {
                        this.setOpacity(key, val);
                    } else {
                        this.setOpacity(key, 100);
                    }
                });
            }
        }
    };
}
